/**
 * Phone-app data model — PURE (no React, no DOM) so it is unit-tested before it touches a screen.
 *
 * A phone project = one or more gallery videos ("sources") turned into ONE list of clip rows
 * in the exact shape the web ET uses (id, sentence, start_in/end_in in SECONDS, sec, speaker,
 * sentiment, …). Several videos are merged with the web archive's own module
 * (grid/multiSheet.js): every id is namespaced `${sourceId}_${clipId}` and every row carries a
 * `source` ({ transcriptId, short: "V1", color, video: blob url, … }). That is what lets the
 * same speaker-linking, AI-pack and per-source-export modules work on the phone later without
 * a second implementation.
 *
 * Two ways to combine several videos:
 *  - SEPARATE: each video keeps its own clock; rows show V1/V2 badges and colour stripes.
 *  - STITCH:   one continuous clock (`tl_start`/`tl_end` = local time + the offset of every
 *              video before it, in pick order). Files are NOT concatenated on the phone; the
 *              stitch is virtual until export, which concatenates the picked segments.
 *
 * Chop = the web ET's "Segment by → every N seconds", made a first-class import option. The
 * windows are cut from the video's duration alone (no transcript needed), so a user can share
 * pieces in under a minute with no transcription at all.
 */
import { mergeSheets, parseSheetId } from "../grid/multiSheet";

export const CHOP_SECONDS = [2, 5, 10];
export const DEFAULT_CHOP = 5;
export const MODES = { SEPARATE: "separate", STITCH: "stitch" };

/** A trailing sliver shorter than this fraction of the window is folded into the previous clip. */
const SLIVER_FRACTION = 0.2;

const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

/** HH:MM:SS.mmm — matches regroupSentences.js / createVideo.jsx's secondsToHMS. */
export function hms(totalSeconds) {
  const t = Math.max(0, Number(totalSeconds) || 0);
  const ms = Math.round((t % 1) * 1000);
  const s = Math.floor(t) % 60;
  const m = Math.floor(t / 60) % 60;
  const h = Math.floor(t / 3600) % 24;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

/** Short clock for pills and cards: 0:05, 1:05, 1:00:05. */
/**
 * What to tell someone while CherryClipz transcribes their video.
 *
 * The wait has no percentage — AssemblyAI reports no progress — so the screen used to show a
 * ring frozen at 0%, which is indistinguishable from a hang. Twice that made a working upload
 * look broken. These say which of the two real stages the server is in, and the screen pairs
 * them with elapsed time so something visibly moves.
 */
export function waitLabel(status) {
  const s = String(status || "").trim().toLowerCase();
  if (!s) return "Sent. Waiting for CherryClipz.";
  // NOTE: a video stays "pending" for the WHOLE server-side journey. Nothing in the
  // transcription path ever writes "in_progress" (checked in tasks.py) — the row goes straight
  // from pending to transcribe completed. So this label must be true of queueing AND
  // transcribing; saying "in the queue" would read as stuck once work had actually started.
  if (s === "pending" || s === "add to tasks") return "CherryClipz is working on it";
  if (s === "in_progress") return "Writing down every word";
  if (s.includes("fail")) return "That did not work";
  return "Working on it";
}

/**
 * A source that streams from the library instead of living on the phone.
 *
 * Same shape the picker produces, so every screen treats it like any other video: `url` plays
 * it, `duration` sizes it. Two differences a screen may care about: `remote` is true, and
 * `path` is empty — there is no file on the phone, so on-device export cannot cut it (the
 * server can, and that is where captions live). `transcriptId` is what a later launch uses to
 * fetch a fresh signed link, since the one in `url` is good for a day.
 */
export function remoteSource(lib, { id = 1, duration = 0 } = {}) {
  return {
    id,
    name: lib.title || "Video",
    path: "",
    url: lib.video || "",
    duration: Number(lib.duration) || Number(duration) || 0,
    width: 0,
    height: 0,
    remote: true,
    audioOnly: !!lib.audioOnly,
    transcriptId: lib.transcriptId,
  };
}

/**
 * How a trim changed a clip, for the list to show honestly.
 * Returns null when there is no trim or it equals the original cut; otherwise the new span
 * and the change at each end in seconds (negative = earlier, positive = later).
 */
export function trimDelta(row, trim) {
  if (!row || !trim) return null;
  const start = Number(trim.start), end = Number(trim.end);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const dStart = Math.round((start - row.start_in) * 10) / 10;
  const dEnd = Math.round((end - row.end_in) * 10) / 10;
  if (dStart === 0 && dEnd === 0) return null;
  return { start, end, dStart, dEnd };
}

/** "+1.2s" / "−0.5s" / "" — a signed change, tenths, with a real minus sign. */
export function fmtDelta(d) {
  const v = Math.round((Number(d) || 0) * 10) / 10;
  if (v === 0) return "";
  return `${v > 0 ? "+" : "−"}${Math.abs(v).toFixed(1)}s`;
}

/** The length of a video as far as its sentences reach — a fallback when the server has no duration. */
export function spanOf(sentences) {
  return (sentences || []).reduce((m, s) => Math.max(m, Number(s.end) || 0), 0);
}

/**
 * Which word is being spoken at time `t`, given a clip's `utterance` ([start, end] per word,
 * in the order of the sentence's words). -1 before the first word or with no timings; between
 * words it stays on the word just finished, so the highlight does not flicker in the gaps.
 */
export function wordAt(utterance, t) {
  if (!Array.isArray(utterance) || !utterance.length || !Number.isFinite(t)) return -1;
  let idx = -1;
  for (let i = 0; i < utterance.length; i += 1) {
    const w = utterance[i];
    if (!w || w[0] > t) break;
    idx = i;
  }
  return idx;
}

/**
 * Where a tap on the trim bar should take the playhead.
 *
 * `fraction` is 0..1 across the whole bar, which spans `win` (the reach window, wider than the
 * cut). A tap inside the kept range [start, end) is a seek to that time. A tap outside it is
 * ignored (returns null): that area belongs to the handles, and jumping there would play audio
 * the clip does not include, which is exactly the confusion the playhead exists to remove.
 */
export function tapTime(fraction, win, range) {
  const f = Math.min(Math.max(Number(fraction) || 0, 0), 1);
  const span = Math.max(0.001, win.hi - win.lo);
  const t = win.lo + f * span;
  if (t < range.start || t >= range.end) return null;
  return t;
}

export function fmtClock(totalSeconds) {
  const t = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export const fmtSpan = (start, end) => `${fmtClock(start)}–${fmtClock(end)}`;

/**
 * Cut ONE video into fixed N-second clip rows (ids 1..n, raw — namespacing happens in
 * buildProject). Same row shape as the ET's interval regroup (`_interval: true`).
 */
export function chopClips(duration, seconds) {
  const N = Math.max(0.5, Number(seconds) || DEFAULT_CHOP);
  const total = Number(duration) || 0;
  if (!(total > 0)) return [];
  const out = [];
  let i = 0;
  for (let t = 0; t < total - 1e-6; t += N, i++) {
    let end = Math.min(t + N, total);
    const remaining = total - end;
    // Fold a tiny last sliver into this clip rather than shipping a 0.1s "clip".
    if (remaining > 0 && remaining < N * SLIVER_FRACTION) end = total;
    const dur = end - t;
    out.push({
      id: i + 1,
      sentence: "",
      speaker: "",
      sentiment: "neutral",
      start_in: round3(t),
      end_in: round3(end),
      duration: [hms(t), hms(end)],
      duration_ms: Math.round(dur * 1000),
      sec: round3(dur),
      sec_int: Math.ceil(dur),
      sec_float: parseFloat(dur.toFixed(2)),
      length: 0,
      words: 0,
      utterance: [],
      question: false,
      space: false,
      _interval: true,
      _chop: N,
    });
    if (end >= total) break;
  }
  return out;
}

/**
 * Turn a transcript into clip rows: ONE CLIP PER SENTENCE, which is the whole idea of the ET —
 * the cut points are where people actually stop talking, not an arbitrary stopwatch.
 *
 * The recogniser gives words with timings; `sentencesFromWords` groups them into sentences on
 * end punctuation or a real pause. The offline model emits NO punctuation, so the pause is what
 * actually does the work — hence a threshold near the gap between spoken sentences (0.4s) rather
 * than the longer one you would use on punctuated text, plus a character cap so an unbroken
 * monologue still becomes clips you can tap instead of one long block. Rows come out in exactly the same shape as `chopClips`, so
 * everything downstream (merge, ids, posters, picks, trims, export) is untouched.
 *
 * @param {Array<{text:string, start:number, end:number}>} words  seconds
 */
export function sentencesFromWords(words, { minGap = 0.12, gapFactor = 2, maxChars = 90 } = {}) {
  const list = (words || [])
    .filter((w) => w && w.text && Number.isFinite(w.start))
    .sort((a, b) => a.start - b.start);
  if (!list.length) return [];

  // How long a pause counts as the end of a sentence, measured against how this person is
  // actually speaking. A fixed threshold does not survive contact with real audio: in a
  // measured on-device run the two real sentence breaks were pauses of only 0.15s and 0.18s
  // while every other gap was 0, so anything tuned for a slow speaker merged the lot into one
  // clip. Taking a multiple of the average gap adapts to the speaker, and the floor stops a
  // fast talker being shredded into fragments.
  const gaps = list.slice(1).map((w, i) => Math.max(0, w.start - list[i].end));
  const mean = gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 0;
  const gapLimit = Math.max(minGap, mean * gapFactor);

  const out = [];
  let cur = [];
  const flush = () => {
    if (!cur.length) return;
    out.push({
      text: cur.map((w) => w.text).join(" ").replace(/\s+/g, " ").trim(),
      start: round3(cur[0].start),
      end: round3(cur[cur.length - 1].end),
      words: cur.map((w) => ({ start: round3(w.start), end: round3(w.end) })),   // for the highlight
    });
    cur = [];
  };
  list.forEach((w, i) => {
    cur.push(w);
    const next = list[i + 1];
    const endsSentence = /[.!?…]"?$/.test(w.text);           // if a model ever gives punctuation
    const longPause = next ? next.start - w.end >= gapLimit : true;
    const tooLong = cur.reduce((n, x) => n + x.text.length + 1, 0) > maxChars;
    if (!next || endsSentence || longPause || tooLong) flush();
  });
  flush();
  return out.filter((s) => s.end > s.start && s.text);
}

/** A gap between two sentences worth offering as its own clip (a breath, a pause, a look). */
export const MIN_SILENCE = 0.6;

/**
 * Transcript sentences -> clip rows, with the SILENCES between them kept as rows of their own.
 *
 * The web ET has always carried "space" rows for the same reason: when a video is cut on speech,
 * the quiet parts are where the reaction lives, and an edit that can only use talking sounds
 * clipped. They are marked `space: true` and hidden until asked for, exactly like the website.
 */
export function sentenceClips(sentences, { silences = true } = {}) {
  const clean = (sentences || [])
    .filter((s) => s && Number.isFinite(s.start) && Number.isFinite(s.end) && s.end > s.start)
    .sort((a, b) => a.start - b.start);

  const withGaps = [];
  clean.forEach((s, i) => {
    const prev = clean[i - 1];
    if (silences && prev && s.start - prev.end >= MIN_SILENCE) {
      withGaps.push({ start: prev.end, end: s.start, text: "", gap: true });
    }
    withGaps.push(s);
  });

  return withGaps
    .map((s, i) => {
      const dur = s.end - s.start;
      const text = (s.text || "").trim();
      return {
        id: i + 1,
        sentence: text,
        speaker: s.speaker || "",
        // Only the cloud pass knows how a line feels; the on-device recogniser never does.
        sentiment: s.sentiment || "neutral",
        thumbnail: s.thumbnail || "",     // a server poster, when the sentences came from the cloud
        start_in: round3(s.start),
        end_in: round3(s.end),
        duration: [hms(s.start), hms(s.end)],
        duration_ms: Math.round(dur * 1000),
        sec: round3(dur),
        sec_int: Math.ceil(dur),
        sec_float: parseFloat(dur.toFixed(2)),
        length: text.length,
        words: text ? text.split(/\s+/).length : 0,
        utterance: Array.isArray(s.words) ? s.words.map((w) => [w.start, w.end]) : [],
        question: /\?/.test(text),
        space: false,
        // `_gap`, NOT the web ET's `space`: there a spacer is a non-row the user cannot touch,
        // and multiSheet deliberately skips namespacing it — which would give two videos'
        // gaps colliding ids. Here a gap IS a clip you can keep, trim and export.
        _gap: !!s.gap,
        _spoken: true,
      };
    });
}

/** Case-insensitive search over what was said. The point of the whole app on a phone. */
export function searchRows(rows, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((r) => (r.sentence || "").toLowerCase().includes(q));
}

/** How many clips a chop would make — what the chop pills print under the seconds. */
export const estimateClipCount = (duration, seconds) => chopClips(duration, seconds).length;

/**
 * Build the project's merged row list.
 * @param {{ sources: Array<{ id:number, name:string, url:string, duration:number }>,
 *           mode?: string, chopSeconds?: number }} p
 */
export function buildProject({ sources, mode = MODES.SEPARATE, chopSeconds = DEFAULT_CHOP, transcripts = null } = {}) {
  const list = (sources || []).filter(Boolean);
  const stitched = mode === MODES.STITCH;
  const sheets = list.map((s, index) => {
    const id = s.id != null ? s.id : index + 1;
    // A transcribed video is cut on speech; everything else falls back to the stopwatch.
    const spoken = transcripts && transcripts[String(id)];
    return {
      transcriptId: id,
      label: s.name || `Video ${index + 1}`,
      rows: spoken && spoken.length ? sentenceClips(spoken) : chopClips(s.duration, chopSeconds),
      video: s.url || "",
      remote: !!s.remote,               // streams from the library; no file on the phone
      audioOnly: !!s.audioOnly,         // no picture to show; play it as sound
      duration: Number(s.duration) || 0,
    };
  });

  const offsets = {};
  let acc = 0;
  sheets.forEach((sh) => {
    offsets[String(sh.transcriptId)] = round3(acc);
    acc += sh.duration;
  });

  const rows = mergeSheets(sheets).map((r) => {
    const off = stitched ? offsets[String(r.source.transcriptId)] || 0 : 0;
    return { ...r, tl_start: round3(r.start_in + off), tl_end: round3(r.end_in + off) };
  });

  // One `source` object per sheet (mergeSheets builds them; read them back off the rows so the
  // colours/badges are exactly what the rows carry). A zero-length video has no rows → dropped.
  const seen = new Map();
  rows.forEach((r) => {
    const k = String(r.source.transcriptId);
    if (!seen.has(k)) seen.set(k, r.source);
  });

  // The NATIVE file path per source, kept beside the rows rather than on them. A row's
  // `source.video` is the URL the WebView plays (https://localhost/_capacitor_file_/…), which
  // the encoder cannot open — it needs the real path. Handing it the playback URL is exactly
  // how the first export attempt failed, with "Failed to instantiate extractor".
  const paths = {};
  list.forEach((s, index) => {
    const id = String(s.id != null ? s.id : index + 1);
    if (s.path) paths[id] = s.path;
  });

  return {
    mode: stitched ? MODES.STITCH : MODES.SEPARATE,
    chopSeconds: Math.max(0.5, Number(chopSeconds) || DEFAULT_CHOP),
    spoken: !!(transcripts && Object.keys(transcripts).length),
    paths,
    sources: [...seen.values()],
    rows,
    offsets,
    totalDuration: round3(acc),
    clipCount: rows.length,
  };
}

/** "2_7" → "2" (the source the clip was cut from); null for anything that isn't namespaced. */
export const clipSourceId = (clipId) => {
  const p = parseSheetId(clipId);
  return p ? p.transcriptId : null;
};

/**
 * Trims. A trim is stored per clip id as ABSOLUTE seconds inside that clip's own source video
 * ({ start, end }), not as deltas — so a trim survives a re-order and reads the same as the
 * clip's own start_in/end_in. `clampTrim` keeps a trim inside its clip and above MIN_CLIP.
 */
export const MIN_CLIP = 0.3; // seconds — below this a clip is not worth a frame

/**
 * How far outside its own cut a clip may be stretched. A pre-cut boundary is an arbitrary
 * stopwatch tick or the end of a sentence, and the moment you actually want usually starts a
 * breath earlier — so the handles reach into the source video either side, not just inside the
 * piece we happened to slice. The only real limits are the file itself and a sane window.
 */
export const REACH = 8; // seconds either side of the original cut

/** The full span the handles may move in: the clip, opened out, clipped to the file. */
export function trimWindow(row) {
  const dur = (row && row.source && Number(row.source.duration)) || 0;
  const hardEnd = dur > 0 ? dur : row.end_in + REACH;
  return {
    lo: Math.max(0, row.start_in - REACH),
    hi: Math.min(hardEnd, row.end_in + REACH),
  };
}

export function clampTrim(row, trim) {
  if (!row) return null;
  const { lo, hi } = trimWindow(row);
  let start = trim && trim.start != null ? Number(trim.start) : row.start_in;
  let end = trim && trim.end != null ? Number(trim.end) : row.end_in;
  if (!Number.isFinite(start)) start = row.start_in;
  if (!Number.isFinite(end)) end = row.end_in;
  start = Math.min(Math.max(start, lo), hi - MIN_CLIP);
  end = Math.max(Math.min(end, hi), start + MIN_CLIP);
  return { start: round3(start), end: round3(end) };
}

/** True when this clip is not showing its full original range. */
export const isTrimmed = (row, trim) => {
  if (!trim) return false;
  const t = clampTrim(row, trim);
  return Math.abs(t.start - row.start_in) > 0.01 || Math.abs(t.end - row.end_in) > 0.01;
};

/** A row → the timeline's Pick shape (EtTimelineDock.tsx: start/end in SECONDS, own `video`). */
export function pickFromRow(row, order, thumb = "", trim = null) {
  const t = clampTrim(row, trim);
  return {
    id: row.id,
    order,
    start: t.start,
    end: t.end,
    sec: round3(t.end - t.start),
    trimmed: isTrimmed(row, trim),
    sentence: row.sentence || "",
    speaker: row.speaker || "",
    sentiment: row.sentiment || "neutral",
    thumb: thumb || "",
    video: (row.source && row.source.video) || "",
    sourceId: (row.source && row.source.transcriptId) != null ? String(row.source.transcriptId) : "",
  };
}

/** Ticked ids (in tick order) → Picks with order 1..n; ids that no longer exist are skipped. */
export function picksInOrder(rows, selectedIds, thumbs = {}, trims = {}) {
  const byId = new Map((rows || []).map((r) => [String(r.id), r]));
  const out = [];
  (selectedIds || []).forEach((id) => {
    const row = byId.get(String(id));
    if (row) out.push(pickFromRow(row, out.length + 1, thumbs[id], trims[id]));
  });
  return out;
}

/** Move a pick to a new position in the ordered id list (drag to re-order the timeline). */
export function movePick(selectedIds, from, to) {
  const list = [...(selectedIds || [])];
  if (from < 0 || from >= list.length) return list;
  const dest = Math.min(Math.max(to, 0), list.length - 1);
  const [item] = list.splice(from, 1);
  list.splice(dest, 0, item);
  return list;
}

export const totalPickedSeconds = (picks) =>
  round3((picks || []).reduce((a, p) => a + Math.max(0, (p.end || 0) - (p.start || 0)), 0));

/** Toggle an id in an ORDERED pick list (tick = append, untick = remove; order stays). */
export function togglePick(selectedIds, id) {
  const key = String(id);
  const list = selectedIds || [];
  return list.some((x) => String(x) === key) ? list.filter((x) => String(x) !== key) : [...list, id];
}
